import React, { useState } from 'react';
import './AddMemoryModal.css';

function AddMemoryModal({ onClose, onAdd }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [password, setPassword] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const compressImage = (file, maxWidth = 1920, maxHeight = 1080, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(resolve, 'image/jpeg', quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (selectedFile) => {
    if (selectedFile && selectedFile.type.startsWith('image/')) {
      // Check file size (50MB limit for processing)
      if (selectedFile.size > 50 * 1024 * 1024) {
        alert('File too large. Please choose a file under 50MB.');
        return;
      }
      
      try {
        // Compress large images
        let processedFile = selectedFile;
        if (selectedFile.size > 2 * 1024 * 1024) { // If larger than 2MB
          console.log(`Compressing ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(1)}MB)`);
          processedFile = await compressImage(selectedFile);
          console.log(`Compressed to ${(processedFile.size / 1024 / 1024).toFixed(1)}MB`);
        }
        
        setFile(processedFile);
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result);
        reader.readAsDataURL(processedFile);
      } catch (error) {
        console.error('Error processing image:', error);
        alert('Error processing image. Please try a different file.');
      }
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !file || !password) {
      alert('Please provide a title, photo, and password');
      return;
    }

    setIsUploading(true);

    try {
      // Convert file to base64
      const imageData = await fileToBase64(file);
      
      // Pass the data to parent with password
      await onAdd({
        title,
        description,
        date: date || 'Date not specified',
        imageData
      }, password);

      // Reset form
      setTitle('');
      setDescription('');
      setDate('');
      setPassword('');
      setFile(null);
      setPreview(null);
    } catch (error) {
      alert('Error uploading memory: ' + error.message);
    } finally {
      setIsUploading(false);
    }
  };

  // Helper function to convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="add-memory-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add New Memory</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="memory-form">
          <div className="form-group">
            <label htmlFor="photo">Photo *</label>
            <div 
              className={`file-drop-zone ${dragActive ? 'drag-active' : ''} ${preview ? 'has-preview' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {preview ? (
                <div className="preview-container">
                  <img src={preview} alt="Preview" className="preview-image" />
                  <button 
                    type="button" 
                    className="change-photo-btn"
                    onClick={() => document.getElementById('file-input').click()}
                  >
                    Change Photo
                  </button>
                </div>
              ) : (
                <div className="drop-zone-content">
                  <div className="upload-icon">📸</div>
                  <p>Drag & drop a photo here, or click to select</p>
                  <button 
                    type="button" 
                    className="select-file-btn"
                    onClick={() => document.getElementById('file-input').click()}
                  >
                    Select Photo
                  </button>
                </div>
              )}
              <input
                id="file-input"
                type="file"
                accept="image/*"
                onChange={(e) => handleFileSelect(e.target.files[0])}
                style={{ display: 'none' }}
              />
            </div>
            <small className="file-info">
              Large images will be automatically compressed. Maximum file size: 50MB.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Give this memory a title..."
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Tell the story behind this memory..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label htmlFor="date">Date</label>
            <input
              id="date"
              type="text"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              placeholder="e.g., May 2024, June 15 2024, or just 2024"
            />
            <small className="form-help">You can enter just a year, month & year, or full date</small>
          </div>

          <div className="form-group">
            <label htmlFor="password">Upload Password *</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter upload password..."
              required
            />
            <small className="form-help">Required to add new memories</small>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onClose} disabled={isUploading}>
              Cancel
            </button>
            <button type="submit" className="add-btn" disabled={isUploading}>
              {isUploading ? 'Adding Memory...' : 'Add Memory'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddMemoryModal;
